import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "@reducers/rootReducer";
import ScopeStackModal from "@components/ScopeStackModal/ScopeStackModal";
import NestedItemSelect, {
  Section,
} from "@components/NestedItemSelect/NestedItemSelect";
import "./style.css";
import useGetProject from "./api/useGetProject";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import { Project } from "./types/Project";
import { TextField } from "@components/FormsV2";
import { useGetAccountQuery, useV2CreateBlueprintMutation } from "@generated";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import {
  buildGeneralSection,
  buildPsSection,
  buildMsSection,
  buildExpenseSection,
  buildGovernanceSection,
  buildProductSection,
  buildResourcesSection,
  buildPayload,
} from "./projectSections";

const GENERAL_OPTIONS = [
  "executive-summary",
  "solution-summary",
  "rate-table",
  "payment-term",
];

const buildSections = (project: Project | null): Section[] => [
  buildGeneralSection(GENERAL_OPTIONS),
  buildPsSection(project),
  buildMsSection(project),
  buildExpenseSection(project),
  buildGovernanceSection(project),
  buildProductSection(project),
  buildResourcesSection(project),
];

interface Props {
  projectId: string;
  show: boolean;
  onClose: () => void;
}

const CreateBlueprintModal: React.FC<Props> = ({
  projectId,
  show,
  onClose,
}) => {
  const { accountSlug } = useSelector((state: RootState) => state.slug);
  const { project, isLoading } = useGetProject(projectId);
  const { data: accountData } = useGetAccountQuery({ slug: accountSlug });
  const [createBlueprint, { isLoading: isSubmitting }] =
    useV2CreateBlueprintMutation();

  const [blueprintName, setBlueprintName] = useState<string>("");
  const [sections, setSections] = useState<Section[]>(() =>
    buildSections(project || null)
  );
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [alertVariant, setAlertVariant] = useState<"success" | "danger">(
    "success"
  );

  useEffect(() => {
    if (show && !isLoading && project) setSections(buildSections(project));
  }, [show, isLoading]);

  const accountId =
    (accountData?.data?.attributes as any)?.["account-id"] ??
    accountData?.data?.id;

  const handleSubmit = () => {
    if (!blueprintName.trim()) {
      setAlertVariant("danger");
      setAlertMessage("Blueprint name is required.");
      return;
    }

    const payload = buildPayload(sections, project);

    createBlueprint({
      body: {
        data: {
          type: "blueprints",
          attributes: {
            name: blueprintName.trim(),
            payload,
          },
          relationships: {
            account: {
              data: { id: accountId, type: "accounts" },
            },
          },
        },
      },
    })
      .unwrap()
      .then(() => {
        setAlertVariant("success");
        setAlertMessage("Blueprint created successfully.");
        setTimeout(() => {
          setAlertMessage(null);
          onClose();
        }, 1500);
      })
      .catch((err) => {
        const detail = err?.data?.errors?.[0]?.detail;
        setAlertVariant("danger");
        setAlertMessage(
          detail ?? "Something went wrong creating the blueprint."
        );
      });
  };

  return (
    <>
      {alertMessage && (
        <ToastAlert
          variant={alertVariant}
          text={alertMessage}
          onClose={() => setAlertMessage(null)}
        />
      )}
      <ScopeStackModal
        className="createBlueprintFromProjectModal"
        show={show}
        modalTitle="Create Blueprint from Project"
        modalBody={
          isLoading ? (
            <ScopeStackSpinner />
          ) : (
            <>
              <div className="fields">
                <TextField
                  id="name"
                  label="Blueprint Name"
                  placeholder="Enter Blueprint Name"
                  onChange={(e) => setBlueprintName(e.target.value)}
                />
              </div>
              <div className="context">Customize Blueprint</div>
              <div className="description">
                Select the items you wish to include on this blueprint.
              </div>
              <NestedItemSelect
                sections={sections}
                onSectionsChange={setSections}
              />
            </>
          )
        }
        button1Text="Cancel"
        button2Text={isSubmitting ? "Creating..." : "Create Blueprint"}
        handleClose={onClose}
        handleButton1Click={onClose}
        handleButton2Click={handleSubmit}
        button2Disabled={isSubmitting}
      />
    </>
  );
};

export default CreateBlueprintModal;
